import { usePhoneLayout } from "../lib/PhoneLayout";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Search, Star, ShoppingCart, Menu, X, ChevronDown } from "lucide-react";
import { useStore } from "../store/StoreContext";
import { MENU } from "../lib/nav";
import { api } from "../lib/api";
import SearchOverlay from "./SearchOverlay";
import SocialLinks from "./SocialLinks";
import useDialogFocus from "../lib/useDialogFocus";
import useSiteSettings from "../lib/useSiteSettings";

function Disclosure({ label, depth, children, testId }) {
  const [open, setOpen] = useState(false);
  const base = depth === 0 ? "py-3 text-[22px] font-light" : "py-2 text-[14px] text-charcoal/80";
  return (
    <div>
      <button aria-expanded={open} data-testid={testId} onClick={() => setOpen((o) => !o)} className={`w-full flex items-center justify-between ${base} hover:text-brass transition-colors duration-200`}>
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden pl-5 ml-1 border-l border-line"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const idOf = (label) => label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

function Leaf({ item, onNavigate, depth }) {
  if (item.enabled === false) return null;
  const base = depth === 0 ? "py-3 text-[22px] font-light" : "py-2 text-[14px] text-charcoal/80";
  return (
    <Link to={item.href} onClick={onNavigate} data-testid={`menu-link-${idOf(item.label)}`} className={`block ${base} hover:text-brass transition-colors duration-200`}>
      {item.label}
    </Link>
  );
}

function SubGroup({ item, onNavigate }) {
  if (item.enabled === false) return null;
  return (
    <Disclosure label={item.label} depth={1} testId={`menu-toggle-${idOf(item.label)}`}>
      {item.children.filter((c) => c.enabled !== false).map((c) => <Leaf key={c.label} item={c} onNavigate={onNavigate} depth={2} />)}
    </Disclosure>
  );
}

function TopItem({ item, onNavigate }) {
  if (item.enabled === false) return null;
  if (!item.children) return <Leaf item={item} onNavigate={onNavigate} depth={0} />;
  return (
    <Disclosure label={item.label} depth={0} testId={`menu-toggle-${idOf(item.label)}`}>
      {item.children.filter((c) => c.enabled !== false).map((c) =>
        c.children ? <SubGroup key={c.label} item={c} onNavigate={onNavigate} /> : <Leaf key={c.label} item={c} onNavigate={onNavigate} depth={1} />
      )}
    </Disclosure>
  );
}

export default function Header() {
  const phone = usePhoneLayout();
  const { cartCount, wishlist, setCartOpen } = useStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const reduced = useReducedMotion();
  const menuRef = useDialogFocus(menuOpen, () => setMenuOpen(false));
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(MENU);
  const settings = useSiteSettings();
  const { pathname } = useLocation();

  useEffect(() => {
    if (phone) return undefined;
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [phone]);

  useEffect(() => setMenuOpen(false), [pathname]);

  useEffect(() => {
    api.site("navigation").then((data) => data.header && setMenu(data.header)).catch(() => {});
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`site-header fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
          phone ? "bg-white border-b border-line" : scrolled
            ? "bg-white/85 backdrop-blur-md shadow-[0_8px_30px_-20px_rgba(0,0,0,0.25)]"
            : "bg-gradient-to-b from-white/90 via-white/60 to-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1600px] h-full px-4 sm:px-8 grid grid-cols-3 items-center">
          <div className="flex items-center">
            <button
              data-testid="menu-open-button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="flex min-h-11 min-w-11 items-center gap-3 p-2 -ml-2 hover:text-brass transition-colors duration-200"
            >
              <Menu className="w-6 h-6" strokeWidth={1.25} />
              <span className="hidden sm:inline text-[12px] font-medium tracking-[0.3em] uppercase">Menu</span>
            </button>
          </div>

          <Link to="/" data-testid="header-brand-logo" className="flex items-center justify-center" aria-label="S H & SONS ® — Home">
            <img src={settings.header_logo_image||"/assets/shsons-emblem-original.svg"} alt="S H & SONS ® — official Tree Emblem" width="667" height="700" className="h-[60px] sm:h-16 lg:h-[68px] w-auto object-contain" data-testid="header-monogram" data-phone-brand-mark />
          </Link>

          <div className="flex items-center justify-end gap-1 sm:gap-3">
            <Link to="/wishlist" data-testid="header-wishlist-link" className="relative hidden sm:block min-h-11 min-w-11 p-2 hover:text-brass transition-colors duration-200" aria-label="Wishlist">
              <Star className="w-[22px] h-[22px]" strokeWidth={1.25} />
              {wishlist.length > 0 && (
                <span data-testid="wishlist-count-badge" className="absolute top-0.5 right-0 bg-brass text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center">
                  {wishlist.length}
                </span>
              )}
            </Link>
            <button data-testid="header-search-button" onClick={() => setSearchOpen(true)} className="min-h-11 min-w-11 p-2 hover:text-brass transition-colors duration-200" aria-label="Search">
              <Search className="w-[22px] h-[22px]" strokeWidth={1.25} />
            </button>
            <button data-testid="header-cart-button" onClick={() => setCartOpen(true)} className="relative min-h-11 min-w-11 p-2 hover:text-brass transition-colors duration-200" aria-label="Enquiry basket">
              <ShoppingCart className="w-[22px] h-[22px]" strokeWidth={1.25} />
              {cartCount > 0 && (
                <span data-testid="cart-count-badge" className="absolute top-0.5 right-0 bg-ink text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              data-testid="menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-ink/40 backdrop-blur-[2px] z-[60]"
              onClick={() => setMenuOpen(false)}
            />
            <motion.aside
              ref={menuRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Main menu" data-testid="menu-drawer"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: reduced ? 0 : 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="viewport-drawer fixed left-0 w-[90%] max-w-[420px] bg-white z-[70] flex flex-col"
            >
              <div className="flex items-center justify-between px-6 h-[76px] shrink-0 border-b border-line">
                <button data-testid="menu-close-button" onClick={() => setMenuOpen(false)} aria-label="Close menu" className="flex min-h-11 min-w-11 items-center gap-3 p-2 -ml-2 hover:text-brass transition-colors duration-200">
                  <X className="w-6 h-6" strokeWidth={1.25} />
                  <span className="text-[12px] font-medium tracking-[0.3em] uppercase">Close</span>
                </button>
                <img src={settings.header_logo_image||"/assets/shsons-emblem-original.svg"} alt="S H & SONS ® monogram" width="667" height="700" className="h-14 w-auto object-contain" />
              </div>
              <nav data-lenis-prevent className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-8 py-6" data-testid="main-navigation">
                <Link to="/buyer-access" onClick={()=>setMenuOpen(false)} className="block min-h-11 py-3 mb-3 text-sm underline underline-offset-4">Buyer sign in / request access</Link>
                {menu.filter((item) => item.enabled !== false).map((item) => (
                  <TopItem key={item.label} item={{ ...item, label: item.label === "Catalogue" && (item.children || item.href === "/shop") ? "Products" : item.label }} onNavigate={() => setMenuOpen(false)} />
                ))}
              </nav>
              <div className="px-6 py-4 shrink-0 border-t border-line flex flex-wrap items-center justify-between gap-3">
                <div className="text-[11px] text-mocha leading-relaxed">
                  <a href={`mailto:${settings.email}`} className="hover:text-ink">{settings.email}</a>
                  <br />
                  <a href={`tel:${settings.phone_primary?.replace(/\s/g, "")}`} className="hover:text-ink">{settings.phone_primary}</a>
                </div>
                <SocialLinks />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
